import { useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * Phase-3 People card payload shapes. Mirror the generated GraphQL types in
 * backend-workspace/apps/gripp-api/generated/index.ts (HomePeopleCard /
 * HomePersonRow) — schema-first, no hand-maintained shapes that drift.
 */
export interface HomePersonRow {
  id: string;
  name: string;
  initials: string;
  /** Role in the scoped workspace, or null if none resolves. */
  role: string | null;
  scans: number;
  replies: number;
}

export interface HomePeopleCard {
  /** Provisional: distinct submitters in range. Pending product definition. */
  activeNow: number;
  totalScans: number;
  totalReplies: number;
  /** Top-N leaderboard, ranked by scans desc, replies desc, name asc. */
  people: HomePersonRow[];
}

const HOME_PEOPLE_QUERY = `
  query HomePeopleCard($filter: HomeActivityFilter) {
    homePeopleCard(filter: $filter) {
      activeNow
      totalScans
      totalReplies
      people {
        id
        name
        initials
        role
        scans
        replies
      }
    }
  }
`;

/**
 * Fetches the People card payload for the current workspace and selected time
 * range. Root query — reads `data.homePeopleCard` directly. Guards
 * `currentWorkspace?.id` (undefined on first render).
 *
 * Cache key: ['homePeopleCard', workspaceId, range] — mirrors useHomeCards.ts.
 * Scoped to the viewed (possibly impersonated) workspace via workspaceId in
 * filter — the resolver enforces it via resolveScope.
 */
export function useHomePeople(range: TimeRange): {
  data: HomePeopleCard | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homePeopleCard', workspaceId, range],
    query: HOME_PEOPLE_QUERY,
    variables: {
      filter: {
        workspaceId,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    },
    options: {
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  });

  return { data: data?.homePeopleCard, isLoading, error: error as Error | null };
}
